import React, { useState, useEffect } from 'react';
import { Calendar, Play, CheckCircle, XCircle, Loader, Users } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import apiService from '../../services/apiService';
import { leaguesAPI } from '../../services/apiService';
import { parseIntOrPrev } from '../../services/formUtils';

const ManualDailyUpdate = ({ leagueId, league, user }) => {
  const HCML_LEAGUE_ID = '95817fde-2e82-4fc2-94bd-615c3e31fa49';
  const isHcmlLeague = leagueId === HCML_LEAGUE_ID;

  const leagueSeason = league?.current_season;
  const [selectedDate, setSelectedDate] = useState('');
  const [seasonYear, setSeasonYear] = useState(leagueSeason);
  const [seasonStart, setSeasonStart] = useState(`${leagueSeason}-03-18`);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [replayMode, setReplayMode] = useState('universal'); // 'universal', 'vanilla', 'hcml', or 'light'
  const [fillLoading, setFillLoading] = useState(false);
  const [fillMessage, setFillMessage] = useState(null);
  const [fillError, setFillError] = useState(null);

  // Sync defaults when league data loads (league is fetched async, may be null on first render)
  useEffect(() => {
    if (league?.current_season && !selectedDate) {
      setSeasonYear(league.current_season);
      setSeasonStart(`${league.current_season}-03-18`);
    }
  }, [league?.current_season]);

  // Derive season_year and season_start from target_date (same logic as backend: month < 3 = previous year)
  useEffect(() => {
    if (selectedDate) {
      const d = new Date(selectedDate);
      const month = d.getMonth() + 1; // 0-indexed
      const year = d.getFullYear();
      const derived = month < 3 ? year - 1 : year;
      setSeasonYear(derived);
      setSeasonStart(`${derived}-03-18`);
    }
  }, [selectedDate]);

  const handleTriggerUpdate = async () => {
    if (!selectedDate) {
      setError('Please select a target date');
      return;
    }

    // 8m.10b defensive null-validation gate.
    // Per 8m.8c: seasonYear uses parseIntOrPrev with leagueSeason prop fallback,
    // so null reach requires parent failure to provide leagueSeason. Defensive
    // gate protects against future parent component changes + ensures admin
    // tool never sends null season_year to backend.
    if (seasonYear === null || seasonYear === undefined) {
      setError('Season year is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResults(null);

      let endpoint;
      let payload;

      if (replayMode === 'universal' || replayMode === 'light') {
        endpoint = `/api/leagues/${leagueId}/admin/trigger-universal-replay`;
        payload = {
          target_date: selectedDate,
          season_year: seasonYear,
          season_start: seasonStart,
          skip_ingest: replayMode === 'light'
        };
      } else if (replayMode === 'hcml') {
        endpoint = `/api/leagues/${leagueId}/admin/trigger-hcml-replay`;
        payload = { target_date: selectedDate };
      } else {
        endpoint = `/api/leagues/${leagueId}/admin/trigger-vanilla-replay`;
        payload = { target_date: selectedDate };
      }

      const response = await apiService.post(endpoint, payload);

      if (response.data.success) {
        // All replay modes return async responses
        if (response.data.message && response.data.message.includes('started')) {
          setResults({
            async: true,
            mode: replayMode,
            message: response.data.message,
            target_date: response.data.target_date,
            season_year: response.data.season_year,
            season_start: response.data.season_start,
            note: response.data.note
          });
        } else {
          // Synchronous response (daily update)
          setResults(response.data.lambda_response);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to trigger ${replayMode} replay`);
    } finally {
      setLoading(false);
    }
  };

  const handleFillLeagueToCapacity = async () => {
    if (!window.confirm('This will create empty teams to fill the league to capacity. Continue?')) {
      return;
    }

    try {
      setFillLoading(true);
      setFillError(null);
      setFillMessage(null);

      const response = await leaguesAPI.fillLeagueToCapacity(leagueId);

      if (response && response.success) {
        setFillMessage(response.message || `Created ${response.teams_created} teams (${response.current_count}/${response.max_teams})`);

        // Clear message after 5 seconds
        setTimeout(() => setFillMessage(null), 5000);
      } else {
        setFillError(response?.message || 'Failed to fill league');
        setTimeout(() => setFillError(null), 5000);
      }
    } catch (err) {
      console.error('Error filling league:', err);
      setFillError(err.response?.data?.detail || 'Failed to fill league');
      setTimeout(() => setFillError(null), 5000);
    } finally {
      setFillLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTaskName = (taskName) => {
    return taskName
      .replace(/_/g, ' ')
      .toUpperCase()
      .replace(/ELIGIBILITY/, 'ELIGIBILITY')
      .replace(/SNAPSHOTS/, 'SNAPSHOTS')
      .replace(/INGESTION/, 'INGESTION');
  };

  const getLambdaLogName = () => {
    if (replayMode === 'universal' || replayMode === 'light') return 'dynasty-dugout-universal-season-replayer';
    if (replayMode === 'hcml') return 'hcml-season-replayer';
    return 'vanilla-season-replayer';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className={`text-3xl font-bold ${dynastyTheme.classes.text.white} mb-2`}>
          Manual Daily Update
        </h1>
        <p className={dynastyTheme.classes.text.neutralLight}>
          Trigger season replay or daily update Lambda for this league.
        </p>
      </div>

      {/* Replay Mode Selector — Universal visible to ALL leagues */}
      <div className={`${dynastyTheme.components.card.base} p-6`}>
        <label className={`block text-sm font-medium ${dynastyTheme.classes.text.white} mb-3`}>
          Replay Mode
        </label>
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={() => setReplayMode('universal')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              replayMode === 'universal'
                ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/50'
                : 'bg-neutral-700 text-neutral-300 hover:bg-neutral-600'
            }`}
          >
            Universal Replay
          </button>
          <button
            onClick={() => setReplayMode('light')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              replayMode === 'light'
                ? 'bg-orange-600 text-white shadow-lg shadow-orange-600/50'
                : 'bg-neutral-700 text-neutral-300 hover:bg-neutral-600'
            }`}
          >
            Recalculate Stats
          </button>
          <button
            onClick={() => setReplayMode('vanilla')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              replayMode === 'vanilla'
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/50'
                : 'bg-neutral-700 text-neutral-300 hover:bg-neutral-600'
            }`}
          >
            Vanilla Replay
          </button>
          {isHcmlLeague && (
            <button
              onClick={() => setReplayMode('hcml')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                replayMode === 'hcml'
                  ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/50'
                  : 'bg-neutral-700 text-neutral-300 hover:bg-neutral-600'
              }`}
            >
              HCML Transaction Replay
            </button>
          )}
        </div>

        {/* Mode description */}
        {replayMode === 'universal' && (
          <div className="mt-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
            <p className="text-sm text-emerald-400">
              <strong>Universal Replay:</strong> Full 18-task pipeline per date. Ingests MLB games,
              calculates all stats, captures snapshots, generates box scores, updates market values,
              z-scores, projections — everything the daily updater does. Resumable: skips dates
              already processed.
            </p>
          </div>
        )}
        {replayMode === 'light' && (
          <div className="mt-3 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
            <p className="text-sm text-orange-400">
              <strong>Recalculate Stats:</strong> Runs the full pipeline (box scores, season stats, rolling stats,
              trending, position eligibility, market values, z-scores) but SKIPS game log ingestion.
              Use when game logs already exist but stats are missing or need recalculation.
              Resumable — picks up where it left off if it times out.
            </p>
          </div>
        )}
        {replayMode === 'vanilla' && (
          <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-sm text-blue-400">
              <strong>Vanilla Replay:</strong> League-specific only. Captures snapshots, generates box scores,
              and updates accrued stats. Does NOT ingest games or calculate season stats.
              Use when game logs already exist from a prior universal replay.
            </p>
          </div>
        )}
        {replayMode === 'hcml' && (
          <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <p className="text-sm text-yellow-400 flex items-start gap-2">
              <span className="text-lg">⚠️</span>
              <span>
                This will apply HCML transactions from CSV before capturing snapshots.
                Use this mode to replay the 2025 HCML season with historical transactions.
              </span>
            </p>
          </div>
        )}
      </div>

      {/* Date & Season Config */}
      <div className={`${dynastyTheme.components.card.base} p-6`}>
        <div className="space-y-4">
          {/* Target Date */}
          <div>
            <label className={`block text-sm font-medium ${dynastyTheme.classes.text.white} mb-2`}>
              Target Date (replay through this date)
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className={`w-full px-4 py-2 rounded-lg ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.border.light} border ${dynastyTheme.classes.text.white}`}
            />
            {selectedDate && (
              <p className={`mt-1 text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                Will process: {formatDate(selectedDate)}
              </p>
            )}
          </div>

          {/* Season fields — only for universal mode */}
          {replayMode === 'universal' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium ${dynastyTheme.classes.text.white} mb-2`}>
                  Season Year
                </label>
                <input
                  type="number"
                  value={seasonYear}
                  onChange={(e) => setSeasonYear(parseIntOrPrev(e.target.value, leagueSeason))}
                  min={2020}
                  max={2030}
                  className={`w-full px-4 py-2 rounded-lg ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.border.light} border ${dynastyTheme.classes.text.white}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${dynastyTheme.classes.text.white} mb-2`}>
                  Season Start Date
                </label>
                <input
                  type="date"
                  value={seasonStart}
                  onChange={(e) => setSeasonStart(e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.border.light} border ${dynastyTheme.classes.text.white}`}
                />
              </div>
            </div>
          )}

          {/* Run Button */}
          <button
            onClick={handleTriggerUpdate}
            disabled={loading || !selectedDate}
            className={`${dynastyTheme.utils.getComponent('button', 'primary', 'md')} flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                Run {replayMode === 'universal' ? 'Universal' : replayMode === 'hcml' ? 'HCML' : 'Vanilla'} Replay
              </>
            )}
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className={`${dynastyTheme.components.card.base} p-4 border-2 border-red-500/50`}>
          <div className="flex items-start gap-3">
            <XCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-red-400 mb-1">Error</h3>
              <p className={dynastyTheme.classes.text.neutralLight}>{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Results Display */}
      {results && (
        <div className={`${dynastyTheme.components.card.base} p-6`}>
          {results.async ? (
            // Async processing message (all replay modes)
            <>
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle className={`w-8 h-8 ${
                  results.mode === 'universal' ? 'text-emerald-400' :
                  results.mode === 'hcml' ? 'text-purple-400' : 'text-blue-400'
                }`} />
                <div>
                  <h2 className={`text-2xl font-bold ${dynastyTheme.classes.text.white}`}>
                    {results.mode === 'universal' ? 'Universal' :
                     results.mode === 'hcml' ? 'HCML' : 'Vanilla'} Replay Started
                  </h2>
                  <p className={dynastyTheme.classes.text.neutralLight}>
                    Processing through {results.target_date} in background
                    {results.season_start && ` (from ${results.season_start})`}
                  </p>
                </div>
              </div>
              <div className={`p-4 rounded-lg ${
                results.mode === 'universal' ? 'bg-emerald-500/10 border border-emerald-500/30' :
                results.mode === 'hcml' ? 'bg-purple-500/10 border border-purple-500/30' :
                'bg-blue-500/10 border border-blue-500/30'
              }`}>
                <p className={`font-semibold mb-2 ${
                  results.mode === 'universal' ? 'text-emerald-400' :
                  results.mode === 'hcml' ? 'text-purple-400' : 'text-blue-400'
                }`}>{results.message}</p>
                <p className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                  {results.note}
                </p>
                <p className={`text-sm text-yellow-400 mt-3 font-semibold`}>
                  ⚠️ This confirms the Lambda was invoked — not that it completed successfully.
                  Check CloudWatch logs to verify it is actually processing dates.
                </p>
                <p className={`text-sm ${dynastyTheme.classes.text.neutralLight} mt-2 font-semibold`}>
                  ⏱️ This may take 5-15 minutes depending on date range.
                </p>
                <p className={`text-sm ${dynastyTheme.classes.text.neutralLight} mt-2`}>
                  📊 Refresh the page after completion and check Box Scores to see results.
                </p>
                <p className={`text-sm ${dynastyTheme.classes.text.neutralLight} mt-3`}>
                  💻 To monitor progress in real-time, run:
                </p>
                <code className="block mt-2 p-2 bg-neutral-800 rounded text-xs text-green-400">
                  aws logs tail /aws/lambda/{getLambdaLogName()} --follow
                </code>
              </div>
            </>
          ) : (
            // Sync processing results (daily update mode)
            <>
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle className="w-8 h-8 text-green-400" />
            <div>
              <h2 className={`text-2xl font-bold ${dynastyTheme.classes.text.white}`}>
                Update Complete
              </h2>
              <p className={dynastyTheme.classes.text.neutralLight}>
                {results.execution_time_seconds
                  ? `Processed in ${results.execution_time_seconds.toFixed(2)} seconds`
                  : `Completed for ${selectedDate}`}
              </p>
            </div>
          </div>

          {/* Task Results */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(results.tasks ?? {}).map(([taskName, taskResult]) => (
              <div
                key={taskName}
                className={`p-4 rounded-lg border ${
                  taskResult.success
                    ? 'bg-green-500/10 border-green-500/30'
                    : 'bg-red-500/10 border-red-500/30'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className={`font-semibold ${dynastyTheme.classes.text.white} text-sm`}>
                    {formatTaskName(taskName)}
                  </h4>
                  {taskResult.success ? (
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  )}
                </div>
                {taskResult.error && (
                  <p className="text-sm text-red-400">{taskResult.error}</p>
                )}
              </div>
            ))}
          </div>

          {/* Errors Summary */}
          {results.errors && results.errors.length > 0 && (
            <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <h3 className="font-semibold text-red-400 mb-2">Errors:</h3>
              <ul className="list-disc list-inside space-y-1">
                {results.errors.map((error, index) => (
                  <li key={index} className={dynastyTheme.classes.text.neutralLight}>
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          )}
            </>
          )}
        </div>
      )}

      {/* Fill League to Capacity */}
      <div className={`${dynastyTheme.components.card.base} p-6`}>
        <h3 className={`text-lg font-semibold ${dynastyTheme.classes.text.white} mb-3`}>
          Fill League to Capacity
        </h3>
        <p className={`text-sm ${dynastyTheme.classes.text.neutralLight} mb-4`}>
          Instantly create empty teams to fill the league up to max_teams capacity. Useful for testing.
        </p>
        <button
          onClick={handleFillLeagueToCapacity}
          disabled={fillLoading}
          className={`${dynastyTheme.utils.getComponent('button', 'primary', 'md')} flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {fillLoading ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              Filling...
            </>
          ) : (
            <>
              <Users className="w-5 h-5" />
              Fill League to Capacity
            </>
          )}
        </button>
        {fillMessage && (
          <div className={`mt-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg`}>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
              <p className={`text-sm ${dynastyTheme.classes.text.white}`}>{fillMessage}</p>
            </div>
          </div>
        )}
        {fillError && (
          <div className={`mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg`}>
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
              <p className={`text-sm text-red-400`}>{fillError}</p>
            </div>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className={`${dynastyTheme.components.card.base} p-6`}>
        <h3 className={`text-lg font-semibold ${dynastyTheme.classes.text.white} mb-3`}>
          How This Works
        </h3>
        {replayMode === 'universal' ? (
          <ul className={`space-y-2 ${dynastyTheme.classes.text.neutralLight}`}>
            <li>• <strong className="text-emerald-400">Universal Replay:</strong> Full 18-task pipeline per date</li>
            <li>• Ingests MLB games, calculates season + rolling stats, position eligibility</li>
            <li>• Captures lineup snapshots, generates box scores, updates accrued stats</li>
            <li>• Syncs players to leagues, updates market values, z-scores, projections</li>
            <li>• <strong className="text-emerald-400">Resumable:</strong> Skips dates already processed (have box scores)</li>
            <li>• Run to day 90, stop, verify, run again with same range — picks up at day 91</li>
            <li>• <strong className="text-yellow-400">Runs async</strong> — refresh after 5-15 minutes</li>
          </ul>
        ) : replayMode === 'hcml' ? (
          <ul className={`space-y-2 ${dynastyTheme.classes.text.neutralLight}`}>
            <li>• <strong className="text-purple-400">HCML Mode:</strong> Replays transactions from HCML CSV</li>
            <li>• Downloads transaction log from S3</li>
            <li>• Parses and applies adds, drops, activations, and disables</li>
            <li>• Captures lineup snapshots reflecting the transactions</li>
            <li>• Generates box scores for the modified lineups</li>
            <li>• <strong className="text-yellow-400">Runs async</strong> — refresh after 5-10 minutes</li>
          </ul>
        ) : (
          <ul className={`space-y-2 ${dynastyTheme.classes.text.neutralLight}`}>
            <li>• <strong className="text-blue-400">Vanilla Replay:</strong> League-specific only</li>
            <li>• Uses existing game logs (no MLB ingestion, no stats calculation)</li>
            <li>• Captures snapshots, generates box scores, updates accrued stats</li>
            <li>• Use when game logs already exist from a prior universal replay</li>
            <li>• <strong className="text-yellow-400">Runs async</strong> — refresh after 1-2 minutes</li>
          </ul>
        )}
      </div>
    </div>
  );
};

export default ManualDailyUpdate;
